import { useTranslation } from 'react-i18next';
import React, { memo, useMemo, useCallback } from 'react';
import Skeleton from 'react-loading-skeleton';
import cx from 'clsx';
import ReactGA4 from 'react-ga4';

import {
  UserName,
  UnionAvatar,
  EmptyRecord,
  UserCardPopup,
  UnionFollowButtonV2,
  Tips,
} from '@/components';
import { getOfficialPath, isOfficalAddress, openNewWindow, useCurrentUserId } from '@/utils';

import {
  useArticleTopContributors,
  useArticleMyContribution,
  use24hMyContribution,
  use24hTopContributors,
  use24TopXPUser,
  use24MyXp,
} from '../../hooks';
import { useUserType } from '@/hooks';
import { ArticleContributor } from '../../types';
import style from './index.module.scss';

type TopContributorsProps = {
  className?: string;
  id?: number;
  title?: string;
  subTitle?: string;
};

const SkeletonList = memo(({ count = 5 }) => (
  <>
    {Array(count)
      .fill(0)
      .map((_, i) => (
        <div className={style.item} key={i}>
          <Skeleton circle height={32} width={32} className={style.avatar} />
          <div className={style.userName}>
            <Skeleton width={80} />
          </div>
          <Skeleton width={50} className={style.contribution} />
        </div>
      ))}
  </>
));

const Contributor = memo(({ data }: { data: ArticleContributor }) => {
  const userType = useUserType();
  const handleUserClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();

      const userId = data.id;
      const isOfficial = isOfficalAddress(userId);

      ReactGA4.event({
        category: 'Stream Follow Guide',
        action: `Enter_Stream_TopUser_${userType}`,
      });

      openNewWindow(
        isOfficial
          ? `${getOfficialPath(data as OfficialAccount)}/stream`
          : `/profile/${userId}/stream`,
        '_blank',
      );
    },
    [data, userType],
  );
  const handleFollow = useCallback(
    isSuccess => {
      ReactGA4.event({
        category: 'Stream Follow Guide',
        action: `Follow_Stream_TopUser_${userType}_${isSuccess ? 'Success' : 'Fail'}`,
      });
    },
    [userType],
  );

  return (
    <div className={style.item}>
      <div onClick={handleUserClick} className={style.avatar}>
        <UnionAvatar data={data} size={32} userCardPopup />
      </div>
      <div className={style.userName} onClick={handleUserClick}>
        {isOfficalAddress(data.id) ? (
          <div className={style.officialId}>{(data as OfficialAccount).name}</div>
        ) : (
          <UserCardPopup address={data.id}>
            <UserName
              className={style.uid}
              address={data.id}
              name={(data as GeneralAccount).desc!.user.web3_id}
              userDesc={(data as GeneralAccount)?.desc}
            />
          </UserCardPopup>
        )}
        {data?.id ? (
          <UnionFollowButtonV2
            address={data?.id}
            isFollowing={data?.is_following}
            onFollowCallback={handleFollow}
          />
        ) : null}
      </div>
      <div className={style.contribution}>{data._contribution}</div>
    </div>
  );
});

const TopContributorsComp = memo(
  ({
    isLoading,
    className,
    isMyLoading,
    data,
    enableMyContributionQuery,
    myContribution,
    title,
    subTitle,
    isXP,
  }: {
    isLoading?: boolean;
    isMyLoading?: boolean;
    className?: string;
    data?: ArticleContributor[];
    enableMyContributionQuery?: boolean;
    myContribution?: ArticleContributor;
    title?: string;
    subTitle?: string;
    isXP?: boolean;
  }) => {
    const { t } = useTranslation();

    return (
      <div className={cx(style.topContributors, className)}>
        <div className={style.title}>{title || t('TopContributors.top-contributors')}</div>
        <div className={style.subTitle}>{subTitle || t('TopContributors.subTitle')}</div>
        {isLoading ? (
          <SkeletonList />
        ) : data?.length ? (
          <>
            <div className={style.listHeader}>
              <span>{t('TopContributors.1k157')}</span>
              <span className={style.contributionHeader}>
                {isXP ? (
                  t('TopContributors.xp')
                ) : (
                  <>
                    {t('TopContributors.1p2v768')}
                    <Tips title={t('TopContributors.1w4h5zp')} />
                  </>
                )}
              </span>
            </div>
            {data?.map((x, i) => <Contributor data={x} key={i} />)}
          </>
        ) : (
          <EmptyRecord
            content={<span className={style.emptyDesc}>{t('TopContributors.1pijqcf')}</span>}
            iconType="user"
            className={style.emptyList}
          />
        )}

        {enableMyContributionQuery ? (
          <>
            <div className={cx(style.subTitle, style.myContribution)}>
              {isXP ? t('TopContributors.myXP') : t('TopContributors.fzhyx0')}
            </div>
            {myContribution ? (
              <Contributor data={myContribution} />
            ) : isMyLoading ? (
              <SkeletonList count={1} />
            ) : null}
          </>
        ) : null}
      </div>
    );
  },
);

const ArticleTopContributors = ({ className, id }: TopContributorsProps) => {
  const currentAddress = useCurrentUserId();

  const { data, isLoading } = useArticleTopContributors(id!);
  const enableMyContributionQuery = useMemo(() => !!currentAddress, [currentAddress]);

  const { data: myContribution, isLoading: isMyLoading } = useArticleMyContribution(
    enableMyContributionQuery && id!,
  );

  return (
    <TopContributorsComp
      isLoading={isLoading}
      className={className}
      isMyLoading={isMyLoading}
      data={data}
      enableMyContributionQuery={enableMyContributionQuery}
      myContribution={myContribution}
    />
  );
};

const FeedsTopContributors = ({ className }: TopContributorsProps) => {
  const { t } = useTranslation();

  const currentAddress = useCurrentUserId();

  const { data, isLoading } = use24hTopContributors();
  const enableMyContributionQuery = useMemo(() => !!currentAddress, [currentAddress]);

  const { data: myContribution, isLoading: isMyLoading } =
    use24hMyContribution(enableMyContributionQuery);

  return (
    <TopContributorsComp
      isLoading={isLoading}
      className={className}
      isMyLoading={isMyLoading}
      data={data}
      enableMyContributionQuery={enableMyContributionQuery}
      myContribution={myContribution}
      title={t('TopContributors.19u9s08')}
      subTitle={t('TopContributors.s1')}
    />
  );
};

export const XPTopUsers = ({ className }: TopContributorsProps) => {
  const { t } = useTranslation();

  const currentAddress = useCurrentUserId();

  const { data, isLoading } = use24TopXPUser();
  const enableMyContributionQuery = useMemo(() => !!currentAddress, [currentAddress]);

  const { data: myContribution, isLoading: isMyLoading } = use24MyXp();

  return (
    <TopContributorsComp
      isLoading={isLoading}
      className={className}
      isMyLoading={isMyLoading}
      data={data}
      enableMyContributionQuery={enableMyContributionQuery}
      myContribution={myContribution}
      title={t('TopContributors.19u9s08')}
      subTitle={t('TopContributors.24XPSubtitle')}
      isXP
    />
  );
};

export const TopContributors = memo(({ id, ...rest }: TopContributorsProps) => {
  return id ? <ArticleTopContributors id={id} {...rest} /> : <FeedsTopContributors {...rest} />;
});
